import { BaseService, ApiResponse } from '@/common/services/base.service';
import api from '@/lib/axios';

export interface User {
  id: string;
  email: string;
  fullName?: string | null;
  avatarUrl?: string | null;
  roleId?: string;
  active: boolean;
  employmentType?: 'none' | 'clt' | 'pj' | 'estagio';
  createdAt: string;
  lastActivity?: string | null;
  permissions: PermissionData[];
  role?: {
    id: string;
    name: string;
    description?: string;
  } | null;
  teams?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

export interface PermissionData {
  id: string;
  name: string;
  description?: string;
}

export interface CreateUserDto {
  email: string;
  password: string;
  roleId: string;
}

export interface UpdateUserDto {
  email?: string;
  active?: boolean;
  roleId?: string;
  employmentType?: 'none' | 'clt' | 'pj' | 'estagio';
  /** Ao desativar: confirma que a pessoa devolveu os equipamentos que estavam com ela */
  confirmEquipmentReturned?: boolean;
}

/** Equipamento com a pessoa (aviso antes de desativar a conta). */
export interface UserOpenEquipment {
  id: string;
  type: 'withdrawal' | 'return' | 'login';
  batchId: string | null;
  quantityRequested: number;
  approvedAt: string | null;
  plannedReturnDate: string | null;
  isOverdue: boolean;
  asset: { id: string; code: string | null; name: string };
}

/** Código do 409 quando a conta tem equipamento sem devolução confirmada. */
export const USER_HAS_OPEN_EQUIPMENT = 'USER_HAS_OPEN_EQUIPMENT';

export interface ResetPasswordDto {
  newPassword?: string;
  sendEmail?: boolean;
}

export interface SendAlertDto {
  subject: string;
  message: string;
}

export interface BirthdayUser {
  id: string;
  email: string;
  fullName: string;
  birthDate: string | null;
  nextBirthday: string | null;
  daysUntilBirthday: number | null;
}

export class UsersService extends BaseService<User, CreateUserDto, UpdateUserDto> {
  constructor() {
    super('/users');
  }

  async deactivate(id: string, confirmEquipmentReturned = false): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`${this.baseUrl}/${id}/deactivate`, {
      confirmEquipmentReturned,
    });
    return response.data;
  }

  async getOpenEquipment(
    id: string,
  ): Promise<{ items: UserOpenEquipment[]; pendingCount: number }> {
    const response = await api.get<{ items: UserOpenEquipment[]; pendingCount: number }>(
      `${this.baseUrl}/${id}/open-equipment`,
    );
    return response.data;
  }

  async activate(id: string): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`${this.baseUrl}/${id}/activate`);
    return response.data;
  }

  async resetPassword(
    id: string,
    data: ResetPasswordDto,
  ): Promise<ApiResponse<User & { temporaryPassword?: string }>> {
    const response = await api.post<
      ApiResponse<User & { temporaryPassword?: string }> | (User & { temporaryPassword?: string })
    >(`${this.baseUrl}/${id}/reset-password`, data);

    // Se o backend retornar o usuário diretamente, envolver em ApiResponse
    if ('data' in response.data) {
      return response.data;
    }

    return { data: response.data };
  }

  async sendAlert(id: string, data: SendAlertDto): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${id}/send-alert`,
      data,
    );
    return response.data;
  }

  async getAll(params?: {
    teamId?: string;
    search?: string;
    activeFilter?: string;
  }): Promise<ApiResponse<User[]>> {
    const response = await api.get<User[] | ApiResponse<User[]>>(this.baseUrl, { params });
    if (Array.isArray(response.data)) {
      return { data: response.data };
    }
    return response.data as ApiResponse<User[]>;
  }

  async getStats(): Promise<ApiResponse<{
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    usersByTeam: Array<{ teamId: string; teamName: string; userCount: number }>;
    usersWithoutTeam: number;
  }>> {
    const response = await api.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  async getBirthdays(includeWithoutDate = false): Promise<ApiResponse<BirthdayUser[]>> {
    const response = await api.get<BirthdayUser[] | ApiResponse<BirthdayUser[]>>(
      `${this.baseUrl}/birthdays`,
      { params: { includeWithoutDate } },
    );
    if (Array.isArray(response.data)) {
      return { data: response.data };
    }
    return response.data as ApiResponse<BirthdayUser[]>;
  }
}

export const usersService = new UsersService();

